import {
  sqliteStorage,
  flushSqliteStorageWrites,
  wipeSqlitePreservingDeviceLock,
  SQLITE_MIGRATION_FLAG,
} from './sqliteStorage';
import { logoTextOnPrimaryHex } from './customThemePalette';
import { applyThemeFavicon } from './themeFavicon';

// Local storage keys
const STORAGE_KEYS = {
  AUTH_LOCAL_UID: 'rebuildme_auth_local_uid_v1',
  USER_SETTINGS: 'rebuildme_settings',
  ENTRIES: 'rebuildme_entries',
  DRAFT: 'rebuildme_draft',
  PASSCODE: 'rebuildme_passcode',
  PASSCODE_ENABLED: 'rebuildme_passcode_enabled',
  NOTIFICATION_ENABLED: 'rebuildme_notification_enabled',
  NOTIFICATION_TIME: 'rebuildme_notification_time',
  COLOR_THEME: 'rebuildme_color_theme',
  TEXT_SCALE: 'rebuildme_text_scale',
  AI_COMPANION_ENABLED: 'rebuildme_ai_companion_enabled',
  AI_WELCOME_CACHE: 'rebuildme_ai_welcome_cache',
  AI_WELCOME_VISIT_COUNT: 'rebuildme_ai_welcome_visit_count',
  AI_MODEL_READY: 'rebuildme_ai_model_ready_v1',
  AI_MODEL_VERSION: 'rebuildme_ai_model_version_v1',
  SECURITY_TYPE: 'rebuildme_security_type',
  CHAT_SESSIONS: 'rebuildme_pro_chat_sessions',
  IMPORT_PROFILE_SUMMARY: 'rebuildme_pro_import_summary',
  CHAT_PERMISSIONS: 'rebuildme_pro_chat_permissions',
  HOME_CHAT_STATE: 'rebuildme_home_chat_state_v1',
  COACH_DAILY_STATE: 'rebuildme_coach_daily_state_v1',
  AI_TOKEN_USAGE_DAY: 'rebuildme_ai_token_usage_day_v1',
  HISTORY_WEEKLY_SUMMARY_DAY: 'rebuildme_history_weekly_summary_day_v1',
  VOICE_PREFS: 'rebuildme_voice_prefs_v1',
  CUSTOM_THEME_PALETTE: 'rebuildme_custom_theme_palette_v1',
  WEB_ACCESS_LINK_OFFER_SEEN: 'rebuildme_web_access_link_offer_seen_v1',
  WEB_ACCESS_LINK_OFFER_NEVER: 'rebuildme_web_access_link_offer_never_v1',
  REMINDER_QUEUE: 'rebuildme_reminder_queue_v1',
  SUMMARY_DOCS_CACHE: 'rebuildme_summary_docs_cache_v1',
};

// Use a synchronous cache adapter that mirrors native persistence to SQLite.
const localStorage = sqliteStorage;

const queuePrefsSync = () => {
  if (typeof window === 'undefined') return;
  import('./userPrefsCloudSync')
    .then((m) => m.queueUserPrefsCloudSync())
    .catch(() => {});
};

// ============================================================
// UNIFIED SECURITY MANAGEMENT (Mutually Exclusive)
// ============================================================
export const SECURITY_TYPE = {
  NONE: 'none',
  PASSCODE: 'passcode',
  BIOMETRIC: 'biometric'
};

export const getSecurityType = () => {
  const type = localStorage.getItem(STORAGE_KEYS.SECURITY_TYPE);
  console.log('🔒 getSecurityType:', type || SECURITY_TYPE.NONE);
  return type || SECURITY_TYPE.NONE;
};

export const setSecurityType = (type) => {
  console.log('🔒 setSecurityType:', type);
  localStorage.setItem(STORAGE_KEYS.SECURITY_TYPE, type);
};

// ============================================================
// AI Companion settings
// ============================================================
export const isAICompanionEnabled = () => {
  return true;
};

export const isAIModelReady = () => {
  return localStorage.getItem(STORAGE_KEYS.AI_MODEL_READY) === 'true';
};

export const setAIModelReady = (ready) => {
  localStorage.setItem(STORAGE_KEYS.AI_MODEL_READY, ready ? 'true' : 'false');
};

export const getAIModelVersion = () => {
  return localStorage.getItem(STORAGE_KEYS.AI_MODEL_VERSION) || '';
};

export const setAIModelVersion = (version) => {
  localStorage.setItem(STORAGE_KEYS.AI_MODEL_VERSION, String(version || ''));
};

export const setAICompanionEnabled = (enabled) => {
  localStorage.setItem(STORAGE_KEYS.AI_COMPANION_ENABLED, 'true');
};

export const getAIWelcomeCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AI_WELCOME_CACHE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.date === formatDate(new Date())) return data.message;
    return null;
  } catch { return null; }
};

export const saveAIWelcomeCache = (message) => {
  localStorage.setItem(STORAGE_KEYS.AI_WELCOME_CACHE, JSON.stringify({
    date: formatDate(new Date()),
    message,
  }));
};

export const clearAIWelcomeCache = () => {
  localStorage.removeItem(STORAGE_KEYS.AI_WELCOME_CACHE);
};

export const getAIWelcomeVisitCount = () => {
  const value = Number(localStorage.getItem(STORAGE_KEYS.AI_WELCOME_VISIT_COUNT) || '0');
  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const incrementAIWelcomeVisitCount = () => {
  const next = getAIWelcomeVisitCount() + 1;
  localStorage.setItem(STORAGE_KEYS.AI_WELCOME_VISIT_COUNT, String(next));
  return next;
};

export const resetAIWelcomeVisitCount = () => {
  localStorage.removeItem(STORAGE_KEYS.AI_WELCOME_VISIT_COUNT);
};

// ============================================================
// Color themes
// ============================================================
export const COLOR_THEMES = {
  pure: {
    name: 'Pure',
    primary: '#F5F5F5',
    secondary: '#E8E8E8',
    accent: '#D9D9D9',
    light: '#FFFFFF',
    textPrimary: '#333333',
    textSecondary: '#666666',
    logoText: '#333333',
  },
  slate: {
    name: 'Slate',
    primary: '#64748B',
    secondary: '#3D4550',
    accent: '#94A3B8',
    light: '#1E2329',
    textPrimary: '#F1F5F9',
    textSecondary: '#94A3B8',
    logoText: '#F8FAFC',
  },
  sunrise: {
    name: 'Sunrise',
    primary: '#FFDA03',
    secondary: '#FFF7CC',
    accent: '#FFDA03',
    light: '#FFFDF0',
    textPrimary: '#5C3D00',
    textSecondary: '#7A5518',
    logoText: '#5C3D00',
  },
  sunset: {
    name: 'Sunset',
    primary: '#CC5500',
    secondary: '#2A2A2A',
    accent: '#CC5500',
    light: '#0D0D0D',
    textPrimary: '#FFE6CC',
    textSecondary: '#CCAA88',
    logoText: '#FFE6CC',
  },
  wisteria: {
    name: 'Wisteria',
    primary: '#D4C7EB',
    secondary: '#E9E3F5',
    accent: '#8E6ECC',
    light: '#F4F1FA',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  skyblue: {
    name: 'Sky',
    primary: '#B8D4E8',
    secondary: '#D6E8F5',
    accent: '#5BA3D9',
    light: '#EEF6FB',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  sage: {
    name: 'Sage',
    primary: '#C5D9C5',
    secondary: '#DFE9DF',
    accent: '#7BA37B',
    light: '#F0F5F0',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  aubergine: {
    name: 'Aubergine',
    primary: '#5E3B6B',
    secondary: '#3F284A',
    accent: '#C084D4',
    light: '#1E1624',
    textPrimary: '#F3EAF8',
    textSecondary: '#CBB7D7',
    logoText: '#F9F3FF',
  },
  midnightblue: {
    name: 'Midnight',
    primary: '#2F5C8F',
    secondary: '#1C3552',
    accent: '#68B5FF',
    light: '#0E1A2A',
    textPrimary: '#EAF4FF',
    textSecondary: '#B5C9DF',
    logoText: '#F4FAFF',
  },
  forestgreen: {
    name: 'Forest',
    primary: '#3F7A55',
    secondary: '#264A34',
    accent: '#7FD89A',
    light: '#0F1F15',
    textPrimary: '#E8F7EE',
    textSecondary: '#B2CEBC',
    logoText: '#F2FFF5',
  },
};

export const getCustomThemePalette = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CUSTOM_THEME_PALETTE);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || typeof p.primary !== 'string' || typeof p.light !== 'string') return null;
    return {
      ...p,
      logoText: logoTextOnPrimaryHex(p.primary),
    };
  } catch {
    return null;
  }
};

export const saveCustomThemePalette = (palette) => {
  if (!palette || typeof palette.primary !== 'string') return;
  localStorage.setItem(STORAGE_KEYS.CUSTOM_THEME_PALETTE, JSON.stringify(palette));
  queuePrefsSync();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rebuildme-custom-theme-palette-changed'));
  }
};

export const getColorTheme = () => {
  const savedTheme = localStorage.getItem(STORAGE_KEYS.COLOR_THEME);
  if (!savedTheme) return 'midnightblue';

  if (savedTheme === 'custom') {
    if (getCustomThemePalette()) return 'custom';
    localStorage.setItem(STORAGE_KEYS.COLOR_THEME, 'midnightblue');
    return 'midnightblue';
  }

  if (!COLOR_THEMES[savedTheme]) {
    localStorage.setItem(STORAGE_KEYS.COLOR_THEME, 'midnightblue');
    return 'midnightblue';
  }

  return savedTheme;
};

export const saveColorTheme = (theme) => {
  if (theme === 'custom') {
    if (!getCustomThemePalette()) {
      return;
    }
  } else if (!COLOR_THEMES[theme]) {
    return;
  }
  localStorage.setItem(STORAGE_KEYS.COLOR_THEME, theme);
  queuePrefsSync();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rebuildme-color-theme-changed', { detail: { theme } }));
  }
};

/** Resolved colors for splash / passcode / anywhere outside ThemeProvider. */
export const getActiveThemeColorSet = () => {
  const k = getColorTheme();
  if (k === 'custom') {
    return getCustomThemePalette() || COLOR_THEMES.midnightblue;
  }
  return COLOR_THEMES[k] || COLOR_THEMES.midnightblue;
};

/**
 * Push theme CSS variables to :root immediately (splash, post-delete, or shared with ThemeProvider).
 * Falls back to Midnight Blue if the key is unknown or custom palette is missing.
 */
export const applyColorThemeToDocument = (themeKey) => {
  if (typeof document === 'undefined') return;
  let key = typeof themeKey === 'string' ? themeKey : 'midnightblue';
  if (key === 'custom' && !getCustomThemePalette()) key = 'midnightblue';
  if (key !== 'custom' && !COLOR_THEMES[key]) key = 'midnightblue';
  const custom = key === 'custom' ? getCustomThemePalette() : null;
  const themeColors = custom || COLOR_THEMES[key] || COLOR_THEMES.midnightblue;
  const root = document.documentElement;
  root.style.setProperty('--theme-primary', themeColors.primary);
  root.style.setProperty('--theme-secondary', themeColors.secondary);
  root.style.setProperty('--theme-accent', themeColors.accent);
  root.style.setProperty('--theme-light', themeColors.light);
  root.style.setProperty('--theme-btn-bg', themeColors.primary);
  root.style.setProperty('--theme-btn-hover', themeColors.secondary);
  root.style.setProperty('--theme-border', themeColors.secondary);
  root.style.setProperty('--theme-input-bg', `${themeColors.light}80`);
  root.style.setProperty('--theme-text-primary', themeColors.textPrimary || '#2D2D35');
  root.style.setProperty('--theme-text-secondary', themeColors.textSecondary || '#73737D');
  root.style.setProperty('--theme-logo-text', themeColors.logoText || '#F9F9FF');
  document.body.setAttribute('data-theme', key);
  applyThemeFavicon(key);
};

// ============================================================
// Voice (TTS / STT preferences)
// ============================================================

const defaultVoicePrefs = () => ({
  ttsRate: 1,
  ttsLang: 'en-US',
  sttLang: 'en-US',
  ttsVoiceURI: '',
  /** When true, Home chat shows a dictation control where supported. */
  voiceInputEnabled: false,
  /** When true, Home chat may show read-aloud where supported. */
  speakEnabled: false,
});

export const getVoicePrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.VOICE_PREFS);
    if (!raw) return defaultVoicePrefs();
    const parsed = JSON.parse(raw);
    return { ...defaultVoicePrefs(), ...parsed };
  } catch {
    return defaultVoicePrefs();
  }
};

export const saveVoicePrefs = (partial) => {
  const next = { ...getVoicePrefs(), ...partial };
  localStorage.setItem(STORAGE_KEYS.VOICE_PREFS, JSON.stringify(next));
  queuePrefsSync();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rebuildme-voice-prefs-changed'));
  }
  return next;
};

// ============================================================
// Text scaling
// ============================================================
export const TEXT_SCALES = {
  small: 0.93,
  default: 1,
  large: 1.08,
  xlarge: 1.16,
};

export const getTextScale = () => {
  const saved = localStorage.getItem(STORAGE_KEYS.TEXT_SCALE) || 'default';
  return TEXT_SCALES[saved] ? saved : 'default';
};

export const getTextScaleValue = (scaleKey = 'default') => {
  return TEXT_SCALES[scaleKey] || TEXT_SCALES.default;
};

export const applyTextScale = (scaleKey = 'default') => {
  const value = getTextScaleValue(scaleKey);
  document.documentElement.style.setProperty('--app-text-scale', String(value));
};

export const saveTextScale = (scaleKey) => {
  const safeKey = TEXT_SCALES[scaleKey] ? scaleKey : 'default';
  localStorage.setItem(STORAGE_KEYS.TEXT_SCALE, safeKey);
  applyTextScale(safeKey);
  queuePrefsSync();
};

// ============================================================
// MIGRATION
// ============================================================
let _migrationIdCounter = 0;
const generateId = (prefix) => `${prefix}_${Date.now()}_${_migrationIdCounter++}`;

const migrateSettings = (old) => {
  if (!old || old.migrated) return old;

  const acts = old.activities;
  if (!Array.isArray(acts) || acts.length === 0) {
    // Post-account-reset seed uses migrated:false with modern { goals: [] } and no legacy
    // `activities`. The old path treated that as "unmigrated legacy" and set migrated:true,
    // which made Google first-name seeding complete onboarding without the chat.
    return old;
  }

  const goals = acts.map((act, i) => {
    const text = act.timeAmount && act.timeAmount.trim()
      ? `${act.timeAmount.trim()} ${act.name.trim()}`
      : act.name.trim();
    return {
      id: `goal_${i + 1}`,
      text,
      easyText: (act.easyActivity || act.failsafe || '').trim(),
      enabled: true,
      order: i,
    };
  });

  return {
    name: old.name || '',
    goals,
    migrated: true,
  };
};

const migrateEntry = (entry, settings) => {
  if (!entry || entry.migrated) return entry;

  const goals = {};
  const goalList = settings?.goals || [];
  if (entry.activities) {
    entry.activities.forEach((act, i) => {
      if (goalList[i]) {
        goals[goalList[i].id] = {
          completed: !!act.completed,
          easyActivity: !!(act.easyActivity || act.failsafe),
        };
      }
    });
  }

  return {
    goals,
    answer1: entry.didWell || '',
    answer2: entry.grateful || '',
    answer3: entry.pride || '',
    answer4: entry.friend || '',
    answer5: entry.notes || '',
    aiResponse: entry.aiResponse || '',
    aiResponseAt: entry.aiResponseAt || '',
    savedAt: entry.savedAt || '',
    migrated: true,
  };
};

const ensureMigration = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
  if (!raw) return;
  const settings = JSON.parse(raw);
  if (settings.migrated) return;

  const newSettings = migrateSettings(settings);
  localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(newSettings));

  const entriesRaw = localStorage.getItem(STORAGE_KEYS.ENTRIES);
  if (entriesRaw) {
    const entries = JSON.parse(entriesRaw);
    const migrated = {};
    for (const [date, entry] of Object.entries(entries)) {
      migrated[date] = migrateEntry(entry, newSettings);
    }
    localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(migrated));
  }

  const draftsRaw = localStorage.getItem(STORAGE_KEYS.DRAFT);
  if (draftsRaw) {
    const drafts = JSON.parse(draftsRaw);
    const migrated = {};
    for (const [date, draft] of Object.entries(drafts)) {
      migrated[date] = migrateEntry(draft, newSettings);
    }
    localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(migrated));
  }
};

ensureMigration();

// ============================================================
// Settings management
// ============================================================

const normalizeConversationalGoal = (g) => {
  const id = String(g?.id || '').trim() || generateId('goal');
  const text = String(g?.text || '').trim();
  const enabled = g?.enabled !== false;
  const createdAt =
    typeof g?.createdAt === 'string' && g.createdAt.trim()
      ? g.createdAt.trim()
      : new Date().toISOString();
  const lastMentionedAt =
    typeof g?.lastMentionedAt === 'string' ? String(g.lastMentionedAt).trim() : '';
  return { id, text, enabled, createdAt, lastMentionedAt };
};

const normalizeUserPreferences = (raw) => {
  const src = raw && typeof raw === 'object' ? raw : {};
  return {
    coaching_style: String(src.coaching_style || '').trim().slice(0, 220),
    communication_style: String(src.communication_style || '').trim().slice(0, 220),
    dislikes: String(src.dislikes || '').trim().slice(0, 320),
    key_motivators: String(src.key_motivators || '').trim().slice(0, 320),
  };
};

const goalRecordIsConversationalV1 = (g) =>
  g &&
  typeof g.id === 'string' &&
  typeof g.text === 'string' &&
  typeof g.enabled === 'boolean' &&
  typeof g.createdAt === 'string' &&
  typeof g.lastMentionedAt === 'string' &&
  !('easyText' in g) &&
  !('order' in g);

const migrateGoalsInSettings = (parsed) => {
  if (!parsed || !Array.isArray(parsed.goals)) return { settings: parsed, changed: false };
  let changed = false;
  const goals = parsed.goals.map((g) => {
    if (goalRecordIsConversationalV1(g)) return g;
    changed = true;
    return normalizeConversationalGoal(g);
  });
  if (!changed) return { settings: parsed, changed: false };
  return { settings: { ...parsed, goals }, changed: true };
};

export const getSettings = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
  if (!raw) return null;
  const parsed = JSON.parse(raw);
  const { settings: afterGoals, changed: goalsChanged } = migrateGoalsInSettings(parsed);
  let settings = afterGoals;
  let changed = goalsChanged;
  const normalizedPrefs = normalizeUserPreferences(settings?.userPreferences);
  if (JSON.stringify(settings?.userPreferences || {}) !== JSON.stringify(normalizedPrefs)) {
    settings = { ...(settings || {}), userPreferences: normalizedPrefs };
    changed = true;
  }
  if (settings && Object.prototype.hasOwnProperty.call(settings, 'prompts')) {
    const { prompts: _removed, ...rest } = settings;
    settings = rest;
    changed = true;
  }
  if (changed) {
    localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(settings));
    queuePrefsSync();
  }
  return settings;
};

export const saveSettings = (settings) => {
  const next = {
    ...(settings || {}),
    userPreferences: normalizeUserPreferences(settings?.userPreferences),
  };
  localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(next));
  queuePrefsSync();
};

export const getUserPreferences = () => normalizeUserPreferences(getSettings()?.userPreferences);

export const updateUserPreferences = (patch = {}) => {
  const cur = getSettings() || { name: '', goals: [], migrated: true };
  const merged = {
    ...(cur.userPreferences || {}),
    ...(patch && typeof patch === 'object' ? patch : {}),
  };
  saveSettings({ ...cur, userPreferences: normalizeUserPreferences(merged) });
  return normalizeUserPreferences(merged);
};

export const hasCompletedOnboarding = () => {
  const settings = getSettings();
  return settings && settings.name && settings.migrated;
};

export const getEnabledGoals = () => {
  const settings = getSettings();
  if (!settings?.goals) return [];
  return settings.goals
    .filter((g) => g.enabled)
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
};

export const addGoal = (goal) => {
  const settings = getSettings();
  if (!settings) return;
  const goals = settings.goals || [];
  if (goals.length >= 5) return;
  const now = new Date().toISOString();
  goals.push(
    normalizeConversationalGoal({
      id: goal.id || generateId('goal'),
      text: goal.text,
      enabled: goal.enabled !== false,
      createdAt: goal.createdAt || now,
      lastMentionedAt: goal.lastMentionedAt ?? '',
    }),
  );
  saveSettings({ ...settings, goals });
};

export const updateGoal = (goalId, updates) => {
  const settings = getSettings();
  if (!settings) return;
  const u = { ...(updates || {}) };
  delete u.easyText;
  delete u.order;
  settings.goals = (settings.goals || []).map((g) =>
    g.id === goalId ? normalizeConversationalGoal({ ...g, ...u }) : g,
  );
  saveSettings(settings);
};

export const deleteGoal = (goalId) => {
  const settings = getSettings();
  if (!settings) return;
  settings.goals = (settings.goals || []).filter((g) => g.id !== goalId);
  saveSettings(settings);
};

export const deleteGoalByTextMatch = (needleRaw) => {
  const needle = String(needleRaw || '').trim().toLowerCase();
  if (needle.length < 2) return false;
  const settings = getSettings();
  if (!settings?.goals?.length) return false;
  const idx = settings.goals.findIndex((g) => {
    const t = String(g?.text || '').trim().toLowerCase();
    if (!t) return false;
    return t === needle || (needle.length >= 4 && (t.includes(needle) || needle.includes(t)));
  });
  if (idx === -1) return false;
  const id = settings.goals[idx].id;
  deleteGoal(id);
  return true;
};

export const reorderGoals = (newOrder) => {
  const settings = getSettings();
  if (!settings) return;
  settings.goals = newOrder.map((g) => normalizeConversationalGoal(g));
  saveSettings(settings);
};

/** Bump lastMentionedAt when assistant text clearly references a goal line. */
export const touchGoalMentionsFromAssistantReply = (assistantText) => {
  const hay = String(assistantText || '').toLowerCase();
  if (!hay.trim()) return;
  const settings = getSettings();
  if (!settings?.goals?.length) return;
  let touched = false;
  const next = settings.goals.map((g) => {
    if (!g?.enabled) return g;
    const needle = String(g.text || '').trim().toLowerCase();
    if (needle.length < 4) return g;
    if (!hay.includes(needle)) return g;
    touched = true;
    return { ...normalizeConversationalGoal(g), lastMentionedAt: new Date().toISOString() };
  });
  if (touched) saveSettings({ ...settings, goals: next });
};

// ============================================================
// Passcode management (with mutual exclusivity)
// ============================================================
export const getPasscode = () => {
  const passcode = localStorage.getItem(STORAGE_KEYS.PASSCODE);
  console.log('🔑 getPasscode:', passcode ? 'present' : 'null');
  return passcode;
};

export const savePasscode = (passcode) => {
  console.log('🔑 savePasscode');
  localStorage.setItem(STORAGE_KEYS.PASSCODE, passcode);
  setPasscodeEnabled(true);
};

export const clearPasscode = () => {
  console.log('🔑 clearPasscode');
  localStorage.removeItem(STORAGE_KEYS.PASSCODE);
  localStorage.removeItem(STORAGE_KEYS.PASSCODE_ENABLED);
  const currentType = getSecurityType();
  if (currentType === SECURITY_TYPE.PASSCODE) {
    setSecurityType(SECURITY_TYPE.NONE);
  }
};

export const isPasscodeEnabled = () => {
  const securityType = getSecurityType();
  const enabled = securityType === SECURITY_TYPE.PASSCODE;
  console.log('🔑 isPasscodeEnabled:', enabled);
  return enabled;
};

export const setPasscodeEnabled = (enabled) => {
  console.log('🔑 setPasscodeEnabled:', enabled);
  if (enabled) {
    setSecurityType(SECURITY_TYPE.PASSCODE);
    localStorage.setItem('rebuildme_biometrics_enabled', 'false');
  } else {
    const currentType = getSecurityType();
    if (currentType === SECURITY_TYPE.PASSCODE) {
      setSecurityType(SECURITY_TYPE.NONE);
    }
  }
};

// ============================================================
// Biometrics management (with mutual exclusivity)
// ============================================================
export const isBiometricsEnabled = () => {
  const securityType = getSecurityType();
  const enabled = securityType === SECURITY_TYPE.BIOMETRIC;
  console.log('📱 isBiometricsEnabled:', enabled);
  return enabled;
};

export const setBiometricsEnabled = (enabled) => {
  console.log('📱 setBiometricsEnabled:', enabled);
  if (enabled) {
    setSecurityType(SECURITY_TYPE.BIOMETRIC);
    localStorage.removeItem(STORAGE_KEYS.PASSCODE);
    localStorage.removeItem(STORAGE_KEYS.PASSCODE_ENABLED);
  } else {
    const currentType = getSecurityType();
    if (currentType === SECURITY_TYPE.BIOMETRIC) {
      setSecurityType(SECURITY_TYPE.NONE);
    }
  }
};

// ============================================================
// Entries management (WITH PROMPT SNAPSHOTS)
// ============================================================
export const getEntries = () => {
  const entries = localStorage.getItem(STORAGE_KEYS.ENTRIES);
  return entries ? JSON.parse(entries) : {};
};

const parseIsoTime = (value) => {
  if (value && typeof value === 'object' && typeof value._seconds === 'number') {
    return value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1e6);
  }
  const ms = Date.parse(String(value || ''));
  return Number.isFinite(ms) ? ms : 0;
};

/** Merge cloud-fetched journal entries into local storage without re-uploading. */
export const mergeCloudEntriesIntoLocal = (cloudEntries) => {
  const incoming = Array.isArray(cloudEntries) ? cloudEntries : [];
  if (!incoming.length) return 0;

  const local = getEntries();
  let changed = 0;

  for (const row of incoming) {
    const date = String(row?.date || row?.id || '').trim();
    if (!date) continue;

    const localEntry = local[date] || null;
    const localTs = parseIsoTime(localEntry?.updatedAt || localEntry?.savedAt);
    const cloudTs = parseIsoTime(row?.updatedAt || row?.savedAt);

    const shouldReplace = !localEntry || cloudTs >= localTs;
    if (!shouldReplace) continue;

    const { id, ...rest } = row || {};
    const mergedRow = { ...(localEntry || {}), ...rest, date };
    delete mergedRow.promptSnapshots;
    local[date] = mergedRow;
    changed += 1;
  }

  if (changed > 0) {
    localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(local));
  }
  return changed;
};

export const saveEntry = (date, entryData) => {
  const entries = getEntries();
  const existingEntry = entries[date] || {};
  const settings = getSettings();
  const goals = settings?.goals || [];

  const hasExistingGoalSnapshots = Array.isArray(existingEntry.goalSnapshots) && existingEntry.goalSnapshots.length > 0;

  // Preserve historical snapshots if they already exist.
  if (entryData.goalSnapshots) {
    // Caller provided explicit snapshots (e.g. today's entry with updated settings).
  } else if (hasExistingGoalSnapshots) {
    entryData.goalSnapshots = existingEntry.goalSnapshots;
  } else if (!entryData.goalSnapshots) {
    // Snapshot goals at the time of first save when no snapshot is supplied.
    entryData.goalSnapshots = goals.map((g) => ({
      id: g.id,
      text: g.text,
      createdAt: g.createdAt || '',
    }));
  }

  entryData.savedAt = new Date().toISOString();

  // Preserve AI text unless this save explicitly provides a replacement.
  if (entryData.aiResponse === undefined && existingEntry.aiResponse) {
    entryData.aiResponse = existingEntry.aiResponse;
  }
  if (entryData.aiResponseAt === undefined && existingEntry.aiResponseAt) {
    entryData.aiResponseAt = existingEntry.aiResponseAt;
  }
  
  const merged = { ...existingEntry, ...entryData };
  delete merged.promptSnapshots;
  entries[date] = merged;
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
  clearDraft(date);

  if (typeof window !== 'undefined') {
    import('./journalCloudSync')
      .then((m) => m.syncJournalEntryToCloud(date, merged))
      .catch(() => {});
  }
};

export const updateEntryWithAIResponse = (date, aiResponse) => {
  const entries = getEntries();
  if (entries[date]) {
    entries[date].aiResponse = aiResponse;
    entries[date].aiResponseAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
  }
};

export const getEntry = (date) => {
  const entries = getEntries();
  return entries[date] || null;  // ✅ Return as-is, no transformation
};

export const deleteEntry = (date) => {
  const entries = getEntries();
  delete entries[date];
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));

  if (typeof window !== 'undefined') {
    import('./journalCloudSync')
      .then((m) => m.deleteJournalEntryFromCloud(date))
      .catch(() => {});
  }
};

export const getDraft = (date) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  return parsed[date] || null;
};

export const saveDraft = (date, draft) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  parsed[date] = draft;
  localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(parsed));
};

export const clearDraft = (date) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  delete parsed[date];
  localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(parsed));
};

export const addCommentToEntry = (date, commentText) => {
  const entries = getEntries();
  const entry = entries[date];
  if (!entry) return;
  
  if (!entry.comments) {
    entry.comments = [];
  }
  
  entry.comments.push({
    text: commentText.trim(),
    date: formatDate(new Date()),
    timestamp: new Date().toISOString(),
  });
  
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
};

// ============================================================
// Notification settings
// ============================================================
export const getNotificationSettings = () => ({
  enabled: localStorage.getItem(STORAGE_KEYS.NOTIFICATION_ENABLED) === 'true',
  time: localStorage.getItem(STORAGE_KEYS.NOTIFICATION_TIME) || '19:00',
});

export const saveNotificationSettings = (enabled, time) => {
  localStorage.setItem(STORAGE_KEYS.NOTIFICATION_ENABLED, enabled.toString());
  localStorage.setItem(STORAGE_KEYS.NOTIFICATION_TIME, time);
};

// ============================================================
// PRO Chat and Import storage
// ============================================================
export const getProChatSessions = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.CHAT_SESSIONS);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveProChatSessions = (sessions) => {
  localStorage.setItem(STORAGE_KEYS.CHAT_SESSIONS, JSON.stringify(sessions || []));
};

export const getProImportSummary = () => {
  return localStorage.getItem(STORAGE_KEYS.IMPORT_PROFILE_SUMMARY) || '';
};

export const saveProImportSummary = (summary) => {
  localStorage.setItem(STORAGE_KEYS.IMPORT_PROFILE_SUMMARY, summary || '');
};

export const getProChatPermissions = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.CHAT_PERMISSIONS);
  if (!raw) {
    return { photos: false, messages: false, calendar: false };
  }
  try {
    const parsed = JSON.parse(raw);
    return {
      photos: !!parsed.photos,
      messages: !!parsed.messages,
      calendar: !!parsed.calendar,
    };
  } catch {
    return { photos: false, messages: false, calendar: false };
  }
};

export const saveProChatPermissions = (permissions) => {
  const merged = { photos: false, messages: false, calendar: false, ...(permissions || {}) };
  localStorage.setItem(STORAGE_KEYS.CHAT_PERMISSIONS, JSON.stringify(merged));
};

export const getHasSeenWebAccessOffer = () => localStorage.getItem(STORAGE_KEYS.WEB_ACCESS_LINK_OFFER_SEEN) === 'true';

export const setHasSeenWebAccessOffer = (seen = true) => {
  localStorage.setItem(STORAGE_KEYS.WEB_ACCESS_LINK_OFFER_SEEN, seen ? 'true' : 'false');
  queuePrefsSync();
};

/** When true, never show the “link other provider” onboarding dialog again. */
export const getWebAccessLinkOfferNeverAsk = () =>
  localStorage.getItem(STORAGE_KEYS.WEB_ACCESS_LINK_OFFER_NEVER) === 'true';

export const setWebAccessLinkOfferNeverAsk = (never = true) => {
  localStorage.setItem(STORAGE_KEYS.WEB_ACCESS_LINK_OFFER_NEVER, never ? 'true' : 'false');
  queuePrefsSync();
};

export const getCoachDailyState = () => {
  const today = formatDate(new Date());
  const raw = localStorage.getItem(STORAGE_KEYS.COACH_DAILY_STATE);
  let parsed = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }
  if (!parsed || String(parsed.date || '') !== today) {
    const fresh = { date: today, userCoachMessages: 0, reflectionOffered: false };
    localStorage.setItem(STORAGE_KEYS.COACH_DAILY_STATE, JSON.stringify(fresh));
    queuePrefsSync();
    return fresh;
  }
  return {
    date: today,
    userCoachMessages: Math.max(0, Number(parsed.userCoachMessages) || 0),
    reflectionOffered: Boolean(parsed.reflectionOffered),
  };
};

export const recordProCoachUserMessage = () => {
  const s = getCoachDailyState();
  const next = {
    ...s,
    userCoachMessages: Math.max(0, Number(s.userCoachMessages) || 0) + 1,
  };
  localStorage.setItem(STORAGE_KEYS.COACH_DAILY_STATE, JSON.stringify(next));
  queuePrefsSync();
};

export const markReflectionOfferedToday = () => {
  const s = getCoachDailyState();
  localStorage.setItem(
    STORAGE_KEYS.COACH_DAILY_STATE,
    JSON.stringify({ ...s, reflectionOffered: true }),
  );
  queuePrefsSync();
};

// ============================================================
// Home screen chat (per calendar day) + daily AI token budget
// ============================================================
/** When `true`, daily cap and limit toasts are off (usage still tracked). */
export const AI_DAILY_TOKEN_BUDGET_DISABLED = false;
/** Local estimate cap per calendar day (not Vertex context-window size). */
export const AI_DAILY_TOKEN_BUDGET = 1000000;
export const AI_TOKEN_WARN_THRESHOLD = 950000;
/** Coach summary length: ~200–280 words keeps retrieval context lean vs 400+ words (~550+ tokens). */
export const HOME_CHAT_SUMMARY_TARGET_WORDS = 260;

/** Drops assistant rows with no visible text (interrupted stream / stuck “Thinking…”). */
export const sanitizeHomeChatMessages = (messages) => {
  if (!Array.isArray(messages)) return [];
  return messages.filter((m) => {
    if (!m || typeof m !== 'object') return false;
    if (m.role === 'assistant') {
      return String(m.text ?? '').trim().length > 0;
    }
    if (m.role === 'user') {
      const hasText = String(m.text ?? '').trim().length > 0;
      const a = m.attachment;
      const hasPhoto = Boolean(a && typeof a === 'object' && (String(a.storagePath || '').trim() || String(a.url || '').trim()));
      return hasText || hasPhoto;
    }
    return false;
  });
};

/** Raw messages: true if any assistant turn has visible text (onboarding / coach actually replied). */
export const homeChatHasAssistantWithText = (messages) => {
  if (!Array.isArray(messages)) return false;
  return messages.some((m) => m && m.role === 'assistant' && String(m.text ?? '').trim().length > 0);
};

export const getHomeChatState = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.HOME_CHAT_STATE);
  if (!raw) return { date: '', messages: [], welcomeText: '', updatedAt: '' };
  try {
    const parsed = JSON.parse(raw);
    return {
      date: String(parsed.date || ''),
      messages: sanitizeHomeChatMessages(Array.isArray(parsed.messages) ? parsed.messages : []),
      welcomeText: String(parsed.welcomeText || ''),
      updatedAt: String(parsed.updatedAt || ''),
    };
  } catch {
    return { date: '', messages: [], welcomeText: '', updatedAt: '' };
  }
};

export const setHomeChatState = (state) => {
  const safe = {
    date: String(state?.date || ''),
    messages: sanitizeHomeChatMessages(Array.isArray(state?.messages) ? state.messages : []),
    welcomeText: String(state?.welcomeText || ''),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEYS.HOME_CHAT_STATE, JSON.stringify(safe));
  queuePrefsSync();
};

export const getAiTokenUsageDay = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.AI_TOKEN_USAGE_DAY);
  if (!raw) return { date: '', used: 0 };
  try {
    const parsed = JSON.parse(raw);
    return {
      date: String(parsed.date || ''),
      used: Math.max(0, Number(parsed.used) || 0),
    };
  } catch {
    return { date: '', used: 0 };
  }
};

export const addAiTokenUsage = (estimatedTokens, todayStr = formatDate(new Date())) => {
  const cur = getAiTokenUsageDay();
  if (cur.date && cur.date !== todayStr) {
    localStorage.setItem(STORAGE_KEYS.AI_TOKEN_USAGE_DAY, JSON.stringify({ date: todayStr, used: 0 }));
  }
  const latest = getAiTokenUsageDay();
  const base = latest.date === todayStr ? latest.used : 0;
  const used = base + Math.max(0, Math.ceil(Number(estimatedTokens) || 0));
  localStorage.setItem(STORAGE_KEYS.AI_TOKEN_USAGE_DAY, JSON.stringify({ date: todayStr, used }));
  if (AI_DAILY_TOKEN_BUDGET_DISABLED) {
    return { used, warn: false, blocked: false };
  }
  return {
    used,
    warn: used >= AI_TOKEN_WARN_THRESHOLD && used < AI_DAILY_TOKEN_BUDGET,
    blocked: used >= AI_DAILY_TOKEN_BUDGET,
  };
};

/** History screen: one AI “recent days” blurb per local calendar day (refreshes after midnight). */
export const getHistoryWeeklySummaryCache = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.HISTORY_WEEKLY_SUMMARY_DAY);
  if (!raw) return { date: '', text: '' };
  try {
    const parsed = JSON.parse(raw);
    const out = {
      date: String(parsed.date || ''),
      text: String(parsed.text || ''),
      weekKey: String(parsed.weekKey || ''),
      summaries:
        parsed.summaries && typeof parsed.summaries === 'object'
          ? {
            weekly: String(parsed.summaries.weekly || ''),
            monthly: String(parsed.summaries.monthly || ''),
            yearly: String(parsed.summaries.yearly || ''),
          }
          : { weekly: '', monthly: '', yearly: '' },
      rangeKeys:
        parsed.rangeKeys && typeof parsed.rangeKeys === 'object'
          ? {
            weekly: String(parsed.rangeKeys.weekly || ''),
            monthly: String(parsed.rangeKeys.monthly || ''),
            yearly: String(parsed.rangeKeys.yearly || ''),
          }
          : { weekly: '', monthly: '', yearly: '' },
      generatedAtByRange:
        parsed.generatedAtByRange && typeof parsed.generatedAtByRange === 'object'
          ? {
            weekly: String(parsed.generatedAtByRange.weekly || ''),
            monthly: String(parsed.generatedAtByRange.monthly || ''),
            yearly: String(parsed.generatedAtByRange.yearly || ''),
          }
          : { weekly: '', monthly: '', yearly: '' },
    };
    if (!out.summaries.weekly && out.text) out.summaries.weekly = out.text;
    return out;
  } catch {
    return {
      date: '',
      text: '',
      weekKey: '',
      summaries: { weekly: '', monthly: '', yearly: '' },
      rangeKeys: { weekly: '', monthly: '', yearly: '' },
    };
  }
};

export const setHistoryWeeklySummaryCache = (
  calendarDay,
  text,
  { range = 'weekly', weekKey = '', rangeKey = '' } = {},
) => {
  const prev = getHistoryWeeklySummaryCache();
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : 'weekly';
  const summaries = {
    weekly: String(prev.summaries?.weekly || ''),
    monthly: String(prev.summaries?.monthly || ''),
    yearly: String(prev.summaries?.yearly || ''),
  };
  summaries[safeRange] = String(text || '');
  const rangeKeys = {
    weekly: String(prev.rangeKeys?.weekly || ''),
    monthly: String(prev.rangeKeys?.monthly || ''),
    yearly: String(prev.rangeKeys?.yearly || ''),
  };
  if (String(rangeKey || '').trim()) {
    rangeKeys[safeRange] = String(rangeKey || '').trim();
  }
  const generatedAtByRange = {
    weekly: String(prev.generatedAtByRange?.weekly || ''),
    monthly: String(prev.generatedAtByRange?.monthly || ''),
    yearly: String(prev.generatedAtByRange?.yearly || ''),
  };
  generatedAtByRange[safeRange] = new Date().toISOString();
  localStorage.setItem(
    STORAGE_KEYS.HISTORY_WEEKLY_SUMMARY_DAY,
    JSON.stringify({
      date: String(calendarDay || ''),
      text: summaries.weekly || String(text || ''),
      weekKey: String(weekKey || prev.weekKey || ''),
      summaries,
      rangeKeys,
      generatedAtByRange,
    }),
  );
  queuePrefsSync();
};

const readReminderQueue = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.REMINDER_QUEUE);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeReminderQueue = (rows) => {
  localStorage.setItem(STORAGE_KEYS.REMINDER_QUEUE, JSON.stringify(Array.isArray(rows) ? rows : []));
};

export const enqueueReminderMemory = ({
  id,
  text,
  time,
  dueAtIso,
  scheduleMode = 'notification',
}) => {
  const nowIso = new Date().toISOString();
  const item = {
    id: String(id || `rem_${Date.now()}`),
    text: String(text || '').trim().slice(0, 220),
    time: String(time || '').trim().slice(0, 10),
    dueAtIso: String(dueAtIso || ''),
    scheduleMode: String(scheduleMode || 'notification'),
    createdAt: nowIso,
    surfacedAt: '',
  };
  const next = [...readReminderQueue().filter((r) => r && r.id !== item.id), item]
    .slice(-80);
  writeReminderQueue(next);
  return item;
};

export const getDueReminderFollowups = (now = new Date()) => {
  const t = now.getTime();
  return readReminderQueue().filter((r) => {
    if (!r || !r.id || r.surfacedAt) return false;
    const dueMs = Date.parse(String(r.dueAtIso || ''));
    if (!Number.isFinite(dueMs)) return false;
    return dueMs <= t;
  });
};

export const getReminderFollowupContext = (now = new Date()) => {
  const nowMs = now.getTime();
  return readReminderQueue()
    .filter((r) => {
      if (!r || !r.id) return false;
      const dueMs = Date.parse(String(r.dueAtIso || ''));
      if (!Number.isFinite(dueMs) || dueMs > nowMs) return false;
      return true;
    })
    .sort((a, b) => Date.parse(String(b.dueAtIso || '')) - Date.parse(String(a.dueAtIso || '')))
    .slice(0, 3)
    .map((r) => ({
      id: String(r.id || ''),
      text: String(r.text || '').trim().slice(0, 220),
      time: String(r.time || '').trim().slice(0, 10),
      dueAtIso: String(r.dueAtIso || ''),
      scheduleMode: String(r.scheduleMode || 'conversation_only'),
      surfacedAt: String(r.surfacedAt || ''),
    }));
};

export const getSummaryDocsCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SUMMARY_DOCS_CACHE);
    const parsed = raw ? JSON.parse(raw) : {};
    const byRange = parsed && typeof parsed === 'object' ? parsed : {};
    return {
      weekly: byRange.weekly && typeof byRange.weekly === 'object' ? byRange.weekly : {},
      monthly: byRange.monthly && typeof byRange.monthly === 'object' ? byRange.monthly : {},
      yearly: byRange.yearly && typeof byRange.yearly === 'object' ? byRange.yearly : {},
    };
  } catch {
    return { weekly: {}, monthly: {}, yearly: {} };
  }
};

export const upsertSummaryDocCache = (range, key, doc) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : '';
  const safeKey = String(key || '').trim();
  if (!safeRange || !safeKey || !doc || typeof doc !== 'object') return;
  const cache = getSummaryDocsCache();
  cache[safeRange] = cache[safeRange] || {};
  cache[safeRange][safeKey] = {
    ...(cache[safeRange][safeKey] || {}),
    ...doc,
    key: safeKey,
    range: safeRange,
    cachedAt: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEYS.SUMMARY_DOCS_CACHE, JSON.stringify(cache));
};

export const upsertSummaryDocCacheBatch = (range, docs = []) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : '';
  if (!safeRange || !Array.isArray(docs) || !docs.length) return;
  const cache = getSummaryDocsCache();
  cache[safeRange] = cache[safeRange] || {};
  for (const doc of docs) {
    const key = String(doc?.key || '').trim();
    if (!key) continue;
    cache[safeRange][key] = {
      ...(cache[safeRange][key] || {}),
      ...(doc || {}),
      key,
      range: safeRange,
      cachedAt: new Date().toISOString(),
    };
  }
  localStorage.setItem(STORAGE_KEYS.SUMMARY_DOCS_CACHE, JSON.stringify(cache));
};

/** Drop one cached summary doc so the next fetch hits Firestore (e.g. after regenerate). */
export const removeSummaryDocFromCache = (range, key) => {
  const safeRange = ['weekly', 'monthly', 'yearly'].includes(String(range || '')) ? String(range) : '';
  const safeKey = String(key || '').trim();
  if (!safeRange || !safeKey) return;
  const cache = getSummaryDocsCache();
  if (cache[safeRange]?.[safeKey]) {
    delete cache[safeRange][safeKey];
    localStorage.setItem(STORAGE_KEYS.SUMMARY_DOCS_CACHE, JSON.stringify(cache));
  }
};

export const getLongTermMemoryMonthlySnippets = (maxItems = 3, maxCharsEach = 300) => {
  const cache = getSummaryDocsCache();
  const monthly = Object.values(cache.monthly || {})
    .filter((d) => d && typeof d === 'object' && String(d.text || '').trim())
    .sort((a, b) => String(b.generatedAt || '').localeCompare(String(a.generatedAt || '')))
    .slice(0, Math.max(1, Number(maxItems) || 3))
    .map((d) => {
      const text = String(d.text || '').replace(/\s+/g, ' ').trim();
      const clip = text.length > maxCharsEach ? `${text.slice(0, maxCharsEach)}…` : text;
      return `${String(d.key || '')}: ${clip}`;
    });
  return monthly;
};

export const markReminderSurfaced = (id) => {
  const target = String(id || '').trim();
  if (!target) return;
  const next = readReminderQueue().map((r) => (
    r && String(r.id || '') === target
      ? { ...r, surfacedAt: new Date().toISOString() }
      : r
  ));
  writeReminderQueue(next);
};

export const mergeHomeCoachSummaryIntoEntry = (dateStr, summaryText) => {
  if (!dateStr || !String(summaryText || '').trim()) return;
  saveEntry(dateStr, {
    homeChatCoachSummary: String(summaryText || '').trim(),
    homeChatCoachSummaryAt: new Date().toISOString(),
  });
};

// ============================================================
// Backup & Restore
// ============================================================
/**
 * JSON imports cannot restore photos/videos (paths and signed URLs are not portable).
 * Removing attachment metadata avoids empty “ghost” thumbnails in History.
 */
export const stripJournalEntryMediaForImport = (entry) => {
  if (!entry || typeof entry !== 'object') return entry;
  const { attachments: _a, ...rest } = entry;
  return rest;
};

export const stripJournalMediaFromEntriesMap = (entries) => {
  if (!entries || typeof entries !== 'object') return entries;
  const out = {};
  for (const [date, row] of Object.entries(entries)) {
    out[date] = stripJournalEntryMediaForImport(row);
  }
  return out;
};

export const exportAllData = () => ({
  backupFormatVersion: 1,
  version: 3,
  exportedAt: new Date().toISOString(),
  settings: getSettings(),
  entries: getEntries(),
  passcodeEnabled: isPasscodeEnabled(),
  notificationSettings: getNotificationSettings(),
  colorTheme: getColorTheme(),
  textScale: getTextScale(),
  aiCompanionEnabled: isAICompanionEnabled(),
  securityType: getSecurityType(),
  homeChatState: getHomeChatState(),
  aiTokenUsageDay: getAiTokenUsageDay(),
  historyWeeklySummaryCache: getHistoryWeeklySummaryCache(),
  voicePrefs: getVoicePrefs(),
  customThemePalette: getCustomThemePalette(),
});

export const importAllData = (data) => {
  if (data.settings && !data.settings.migrated) {
    const migratedSettings = migrateSettings(data.settings);
    saveSettings(migratedSettings);
    if (data.entries) {
      const entries = {};
      for (const [date, entry] of Object.entries(data.entries)) {
        entries[date] = stripJournalEntryMediaForImport(migrateEntry(entry, migratedSettings));
      }
      localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
    }
  } else {
    if (data.settings) saveSettings(data.settings);
    if (data.entries) {
      localStorage.setItem(
        STORAGE_KEYS.ENTRIES,
        JSON.stringify(stripJournalMediaFromEntriesMap(data.entries)),
      );
    }
  }
  if (data.passcodeEnabled !== undefined) setPasscodeEnabled(data.passcodeEnabled);
  if (data.notificationSettings) {
    saveNotificationSettings(data.notificationSettings.enabled, data.notificationSettings.time);
  }
  if (data.customThemePalette && typeof data.customThemePalette === 'object') {
    saveCustomThemePalette(data.customThemePalette);
  }
  if (data.colorTheme) saveColorTheme(data.colorTheme);
  if (data.textScale) saveTextScale(data.textScale);
  if (data.aiCompanionEnabled !== undefined) setAICompanionEnabled(data.aiCompanionEnabled);
  if (data.securityType) setSecurityType(data.securityType);
  if (data.homeChatState) setHomeChatState(data.homeChatState);
  if (data.aiTokenUsageDay) {
    localStorage.setItem(STORAGE_KEYS.AI_TOKEN_USAGE_DAY, JSON.stringify(data.aiTokenUsageDay));
  }
  if (data.historyWeeklySummaryCache && typeof data.historyWeeklySummaryCache === 'object') {
    const h = data.historyWeeklySummaryCache;
    if (h.summaries || h.rangeKeys || h.generatedAtByRange) {
      localStorage.setItem(
        STORAGE_KEYS.HISTORY_WEEKLY_SUMMARY_DAY,
        JSON.stringify({
          date: String(h.date || ''),
          text: String(h.text || h.summaries?.weekly || ''),
          weekKey: String(h.weekKey || ''),
          summaries: {
            weekly: String(h.summaries?.weekly || h.text || ''),
            monthly: String(h.summaries?.monthly || ''),
            yearly: String(h.summaries?.yearly || ''),
          },
          rangeKeys: {
            weekly: String(h.rangeKeys?.weekly || ''),
            monthly: String(h.rangeKeys?.monthly || ''),
            yearly: String(h.rangeKeys?.yearly || ''),
          },
          generatedAtByRange: {
            weekly: String(h.generatedAtByRange?.weekly || ''),
            monthly: String(h.generatedAtByRange?.monthly || ''),
            yearly: String(h.generatedAtByRange?.yearly || ''),
          },
        }),
      );
      queuePrefsSync();
    } else if (h.date) {
      setHistoryWeeklySummaryCache(h.date, h.text || '');
    }
  }
  if (data.voicePrefs && typeof data.voicePrefs === 'object') {
    saveVoicePrefs(data.voicePrefs);
  }
};

export const clearAllData = () => {
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
};

/** Keys that stay on the device after cloud account deletion (lock + SQLite migration marker). */
const KEEP_LOCAL_KEYS_ON_ACCOUNT_DELETE = new Set([
  STORAGE_KEYS.PASSCODE,
  STORAGE_KEYS.PASSCODE_ENABLED,
  STORAGE_KEYS.SECURITY_TYPE,
  SQLITE_MIGRATION_FLAG,
]);

/**
 * Remove every app-scoped browser key (orphan keys outside STORAGE_KEYS included), except device lock / migration.
 */
const sweepLocalStorageAfterAccountDeletion = () => {
  if (typeof window === 'undefined') return;
  const keep = KEEP_LOCAL_KEYS_ON_ACCOUNT_DELETE;
  const toRemove = [];
  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || keep.has(key)) continue;
    const drop =
      key.startsWith('rebuildme_')
      || key === 'rebuildme_biometrics_enabled'
      || key.startsWith('rbm_');
    if (drop) toRemove.push(key);
  }
  toRemove.forEach((key) => {
    try {
      // Use the app storage adapter so native SQLite mirrors cannot retain stale rows.
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
};

/**
 * After cloud account deletion: wipe account-scoped local data (including native SQLite KV),
 * sweep all `rebuildme_*` / `rbm_*` keys, re-seed default settings, reset theme & text scale.
 * Keeps passcode / security type / SQLite migration flag on device.
 */
export const resetLocalAppAfterAccountDeletion = async () => {
  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      window.sessionStorage.clear();
    } catch {
      /* ignore */
    }
  }

  await flushSqliteStorageWrites();
  await wipeSqlitePreservingDeviceLock();

  sweepLocalStorageAfterAccountDeletion();
  localStorage.removeItem(STORAGE_KEYS.AUTH_LOCAL_UID);

  await flushSqliteStorageWrites();

  saveSettings({
    name: '',
    goals: [],
    migrated: false,
  });
  saveNotificationSettings(false, '19:00');
  saveProChatPermissions({ photos: false, messages: false, calendar: false });
  setHomeChatState({ date: '', messages: [], welcomeText: '' });
  void getCoachDailyState();

  saveColorTheme('midnightblue');
  saveTextScale('default');

  applyColorThemeToDocument('midnightblue');
  try {
    applyThemeFavicon('midnightblue');
  } catch {
    /* ignore */
  }
  if (typeof document !== 'undefined') {
    applyTextScale('default');
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('rebuildme-custom-theme-palette-changed'));
    window.dispatchEvent(new CustomEvent('rebuildme-voice-prefs-changed'));
    window.dispatchEvent(new CustomEvent('rebuildme-color-theme-changed', { detail: { theme: 'midnightblue' } }));
  }

  await flushSqliteStorageWrites();
};

/**
 * Prevent local data leakage between different signed-in cloud accounts
 * on the same device/browser profile.
 */
export const resetLocalAccountCacheOnUserChange = (currentUid) => {
  const uid = String(currentUid || '').trim();
  const previousUid = String(localStorage.getItem(STORAGE_KEYS.AUTH_LOCAL_UID) || '').trim();
  if (!uid) return false;
  if (!previousUid) {
    localStorage.setItem(STORAGE_KEYS.AUTH_LOCAL_UID, uid);
    return false;
  }
  if (previousUid === uid) return false;

  const accountScopedKeys = [
    STORAGE_KEYS.USER_SETTINGS,
    STORAGE_KEYS.ENTRIES,
    STORAGE_KEYS.DRAFT,
    STORAGE_KEYS.AI_WELCOME_CACHE,
    STORAGE_KEYS.AI_WELCOME_VISIT_COUNT,
    STORAGE_KEYS.CHAT_SESSIONS,
    STORAGE_KEYS.IMPORT_PROFILE_SUMMARY,
    STORAGE_KEYS.CHAT_PERMISSIONS,
    STORAGE_KEYS.HOME_CHAT_STATE,
    STORAGE_KEYS.COACH_DAILY_STATE,
    STORAGE_KEYS.AI_TOKEN_USAGE_DAY,
    STORAGE_KEYS.HISTORY_WEEKLY_SUMMARY_DAY,
    STORAGE_KEYS.SUMMARY_DOCS_CACHE,
    STORAGE_KEYS.WEB_ACCESS_LINK_OFFER_SEEN,
  ];
  accountScopedKeys.forEach((key) => localStorage.removeItem(key));
  localStorage.setItem(STORAGE_KEYS.AUTH_LOCAL_UID, uid);
  return true;
};

// ============================================================
// Date helpers
// ============================================================

/** YYYY-MM-DD in the user's local timezone (journal entry keys, AI token day, home chat day, etc.). */
export const formatDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const homeChatUpdatedMs = (h) => {
  const t = Date.parse(String(h?.updatedAt || ''));
  return Number.isFinite(t) ? t : 0;
};

/**
 * When the same account is used on two devices, pick the home chat blob that was saved most recently
 * (or today's local day if calendar days diverge).
 */
export const pickPreferredHomeChatState = (cloudRaw, localRaw) => {
  const local = localRaw && typeof localRaw === 'object'
    ? {
      date: String(localRaw.date || ''),
      welcomeText: String(localRaw.welcomeText || ''),
      messages: sanitizeHomeChatMessages(Array.isArray(localRaw.messages) ? localRaw.messages : []),
      updatedAt: String(localRaw.updatedAt || ''),
    }
    : getHomeChatState();
  const cloud = cloudRaw && typeof cloudRaw === 'object'
    ? {
      date: String(cloudRaw.date || ''),
      welcomeText: String(cloudRaw.welcomeText || ''),
      messages: sanitizeHomeChatMessages(Array.isArray(cloudRaw.messages) ? cloudRaw.messages : []),
      updatedAt: String(cloudRaw.updatedAt || ''),
    }
    : null;
  if (!cloud || !String(cloud.date || '').trim()) return local;
  if (!local || !String(local.date || '').trim()) return cloud;

  const today = formatDate(new Date());
  if (cloud.date !== local.date) {
    if (local.date === today) return local;
    if (cloud.date === today) return cloud;
    return homeChatUpdatedMs(cloud) >= homeChatUpdatedMs(local) ? cloud : local;
  }
  const cMs = homeChatUpdatedMs(cloud);
  const lMs = homeChatUpdatedMs(local);
  if (cMs !== lMs) return cMs >= lMs ? cloud : local;
  const cLen = Array.isArray(cloud.messages) ? cloud.messages.length : 0;
  const lLen = Array.isArray(local.messages) ? local.messages.length : 0;
  if (cLen !== lLen) return cLen >= lLen ? cloud : local;
  return local;
};

export const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};